import type { FC } from 'react';
import { useMemo } from 'react';

import type { Category, Maybe } from '../../types/api';
import { CategoryBadge } from './CategoryBadge';
import type { FacetType } from './facets';
import { FACET_PREFIX } from './facets';

// B2_05 — pills de facettes theme/entity : préfixées D22 (# / @), cliquables vers leur
// page dédiée /theme/<slug> ou /entity/<slug> (routage CategoryBadge facetType, B4_05).
// Le type est conservé PAR facette (une liste fusionnée le perdrait). Dédup par id à
// l'intérieur d'un type. Rien rendu si aucune facette. `max` borne l'encombrement des
// surfaces compactes (blocs presse headline/feed) — D23, décision locale du 2026-07-11.
export type FacetBadgesProps = {
  themes?: Maybe<Maybe<Category>[]>;
  entities?: Maybe<Maybe<Category>[]>;
  /** Nombre total maximum de pills rendues (thèmes d'abord). Absent = toutes. */
  max?: number;
  disableLink?: boolean;
  className?: string;
  /** Classe passée à chaque CategoryBadge (harmonisation avec les pills voisines). */
  badgeClassName?: string;
  /**
   * Rendu « texte simple » (pas de pastille) — surfaces média Corevibe où rubriques et facettes
   * sont toutes en texte (décision Mathias 2026-07-18). Gris au repos, primaire au survol du tag.
   * Absent = pastille historique (variant contained) inchangée pour toutes les autres surfaces.
   */
  plain?: boolean;
};

// Style « texte simple » d'une facette en mode plain : discret (potentiellement nombreux tags).
// Pas de pastille, petite police, gris léger au repos → gris foncé au survol (décision Mathias
// 2026-07-18 : plus discret que la couleur primaire). Casse d'origine préservée (#ia générative,
// @Tesla). Neutralise le pill (padding/radius/fond) via twMerge — les classes gagnent (passées en dernier).
const PLAIN_BADGE_CLASS =
  'p-0 rounded-none bg-transparent text-gray-400 text-xs font-medium normal-case tracking-normal transition-colors hover:text-gray-600';

const dedupe = (facets: Maybe<Maybe<Category>[]> | undefined): Category[] => {
  const seen = new Set<string>();
  return (facets ?? []).filter((facet): facet is Category => {
    if (!facet?.id || seen.has(facet.id)) return false;
    seen.add(facet.id);
    return true;
  });
};

export const FacetBadges: FC<FacetBadgesProps> = ({
  themes,
  entities,
  max,
  disableLink,
  className,
  badgeClassName,
  plain,
}) => {
  const facets = useMemo(() => {
    const list: { facet: Category; facetType: FacetType }[] = [
      ...dedupe(themes).map(facet => ({ facet, facetType: 'theme' as const })),
      ...dedupe(entities).map(facet => ({ facet, facetType: 'entity' as const })),
    ];
    return max != null ? list.slice(0, max) : list;
  }, [themes, entities, max]);

  if (!facets.length) return null;

  // Mode plain : facettes discrètes en ligne, séparées par « · » (ex. #ia générative · @Anthropic).
  const wrapperClass =
    className ?? (plain ? 'flex flex-wrap items-center' : 'flex flex-wrap -mx-1');

  return (
    <div className={wrapperClass}>
      {facets.map(({ facet, facetType }, i) => (
        <span key={`${facetType}-${facet.id}`} className={plain ? 'inline-flex items-center' : ''}>
          {plain && i > 0 && (
            <span className="mx-1.5 text-xs text-gray-400 select-none" aria-hidden>
              ·
            </span>
          )}
          <CategoryBadge
            {...facet}
            title={`${FACET_PREFIX[facetType]}${facet.title ?? facet.slug ?? ''}`}
            slug={facet.slug ?? ''}
            facetType={facetType}
            color="gray"
            variant={plain ? undefined : 'contained'}
            className={badgeClassName ?? (plain ? PLAIN_BADGE_CLASS : 'm-1')}
            disableLink={disableLink}
          />
        </span>
      ))}
    </div>
  );
};
